import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs/Subscription';
import { Subject } from 'rxjs/Subject';
import { NgxSpinnerService } from 'ngx-spinner';

import {
  FormModeEnum,
  ToolbarItem,
  TableSettings,
  NotificationService,
  ModalService,
  BackendService,
  FieldStructure,
  TableActionParams,
} from 'beyond-util';

import { SifarnikBase } from '../sifarnik-base/sifarnik-base';
import { FormCode } from '../../../enums/form-code.enum';
import { UtilServiceClient } from '../../../services/util.service';
import { ActionType } from '../../../enums/action-type.enum';
import { EnumService } from '../../../services/enum.service';
import { KorisniciService } from './services/korisnici.service';
import { KorisniciValidationService } from './services/korisnici-validation.service';

@Component({
  selector: 'app-korisnici',
  templateUrl: './korisnici.component.html',
})
export class KorisniciComponent extends SifarnikBase
  implements OnInit, OnDestroy {
  private _subscriptions: Array<Subscription> = [];
  private _data: Array<any>;

  public showDetails = false;
  public ulogeList: Array<any> = [];

  public validateSubject: Subject<any> = new Subject<any>();
  public validateSubject$ = this.validateSubject.asObservable();

  constructor(
    protected _backendService: BackendService,
    protected _modalService: ModalService,
    protected _notificationService: NotificationService,
    private _enumService: EnumService,
    private _utilService: UtilServiceClient,
    private _korisniciService: KorisniciService,
    private _korisniciValidationService: KorisniciValidationService,
    private _spinner: NgxSpinnerService
  ) {
    super(_backendService, _modalService, _notificationService);
  }

  ngOnInit() {
    setTimeout(() => {
      this._spinner.show();
      this.subscriptions();
      super.ngOnInit();
      this.fetchEnums();
    });
  }

  ngOnDestroy() {
    this._subscriptions.forEach((s: Subscription) => s.unsubscribe());
  }

  subscriptions() {
    this.subscribeToListSettings();
    this.subscribeToDetailsSettings();
    this.subscribeToUpdate();
    this.subscribeToCreate();
    this.subscribeToDelete();
  }

  subscribeToListSettings() {
    this._subscriptions.push(
      this._korisniciService.fetchListSettings$.subscribe(response => {
        this._spinner.hide();

        if (!this.settingsTable) {
          this._korisniciService.alterSettings(response);
          this.setSettings(response['Settings']);
        }
        this._dataCount = response['ServiceData']['DataSumCount'];
        this._data = response['ServiceData']['Data'];
        this.setAllFields();
        this.refreshTable();
      })
    );
  }

  subscribeToDetailsSettings() {
    this._subscriptions.push(
      this._korisniciService.fetchDetailsSettings$.subscribe(response => {
        this._spinner.hide();
        this.fieldStructure = new FieldStructure(response.Settings, 'header');
        this.settingsTableDetails = new TableSettings(
          response.Settings.details[0],
          'table'
        );

        this._korisniciService.alterMandatoryFields(
          this.fieldStructure.getFieldByName('Lozinka'),
          this.order.Id ? false : true
        );

        this.ulogeList = this._korisniciService.getUloge(
          response,
          this.order.Id
        );
        this.setInfobar(true, 'Marina Milenkovic', this.order.DatumIzmene);

        if (this.order.Id) {
          this.onViewModeWithParams(
            response.Privilegije.includes('MSKOR_KOR_CREATE'),
            response.Privilegije.includes('MSKOR_KOR_DELETE'),
            response.Privilegije.includes('MSKOR_KOR_UPDATE')
          );
        }
        this.showDetails = true;
      })
    );
  }

  subscribeToUpdate() {
    this._subscriptions.push(
      this._korisniciService.updateEntity$.subscribe(() => {
        this.changeMode(FormModeEnum.Table);
        this.loadSettings();
        this._spinner.hide();
      })
    );
  }

  subscribeToCreate() {
    this._subscriptions.push(
      this._korisniciService.createEntity$.subscribe(() => {
        this.changeMode(FormModeEnum.Table);
        this.loadSettings();
        this._spinner.hide();
      })
    );
  }

  subscribeToDelete() {
    this._subscriptions.push(
      this._korisniciService.deleteEntity$.subscribe(() => {
        this._spinner.hide();
        this.subFilterPage();
        this.changeMode(FormModeEnum.Table);
        this.loadSettings();
      })
    );
  }

  loadSettings() {
    this._korisniciService.fetchListSettings(this.filterQuery);
  }

  fetchEnums() {
    this._enumService.fetchUloge();
  }

  changeName() {
    this.name = this._utilService.getName(
      this.order,
      this.mode,
      'Novi korisnik',
      'Korisnici'
    );
  }

  refreshTable() {
    this.setId(this._data);

    this.orderListCount = this._dataCount;
    this.orderList = this._data;
  }

  onTableActionFilter(event: any) {
    this._spinner.show();
    super.onTableActionFilter(event);
  }

  addOrder() {
    this.setActionType(ActionType.Create);
    this.validateSubject.next();
  }

  create() {
    if (
      !this._korisniciValidationService.validateCreateUpdate(
        this.order,
        this.ulogeList
      )
    ) {
      return;
    }

    this._spinner.show();
    this._korisniciService.create(this.order, this.ulogeList);
  }

  updateOrder() {
    this.setActionType(ActionType.Update);
    this.validateSubject.next();
  }

  update() {
    if (
      !this._korisniciValidationService.validateCreateUpdate(
        this.order,
        this.ulogeList
      )
    ) {
      return;
    }

    this._spinner.show();
    this._korisniciService.update(this.order, this.ulogeList);
  }

  onDeleteConfirm() {
    this._spinner.show();
    this._korisniciService.delete(this.order.Id);
  }

  onTableActionUpdate(event: TableActionParams) {
    super.onTableActionUpdate(event);
    this.refreshTable();
  }

  onTableActionView() {
    this._spinner.show();
    this.setToolbarOptions({
      items: [ToolbarItem.ItemEdit(true, !this.order.status)],
    });

    const orderId = this.order.Id ? this.order.Id : null;
    this._korisniciService.fetchDetailsSettings(FormCode.MSKOR_KOR, orderId);
    this.changeMode(FormModeEnum.View);
  }

  onTableActionAdd() {
    this._spinner.show();
    const orderId = this.order.Id ? this.order.Id : null;
    this._korisniciService.fetchDetailsSettings(FormCode.MSKOR_CKOR, orderId);
    this.changeMode(FormModeEnum.Add);
  }

  onDetailsEditCancel(event) {
    if (this.ulogeList.some(a => a.Id === event.document.Id)) {
      this.copyProperties(
        event.document,
        this.ulogeList.filter(a => a.Id === event.document.Id)[0]
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/pages/sifarnici/korisnici/korisnici.component.ts